import { Cast, Loader2, RefreshCw, Volume2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePlayback } from "../contexts/PlaybackContext";
import { desktopCast, isDesktop, toCastableUrl, type CastDevice } from "../lib/desktop";

/**
 * Desktop cast button, device picker and connected bar.
 *
 * Browsers get their cast dialog free from the Cast SDK; Electron does not
 * have the SDK at all, so every state here is ours to draw. The 12 states and
 * their copy are specified on the `pc/cast-*` artboards — this implements
 * them; the strings below are those strings, not paraphrases.
 *
 * Renders nothing outside the desktop shell.
 */

type Phase = "idle" | "scanning" | "connecting" | "connected" | "blocked";


export function CastPicker() {
  const cast = desktopCast();
  const { source, setCasting } = usePlayback();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<CastDevice[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [connected, setConnected] = useState<CastDevice | null>(null);
  const [volume, setVolume] = useState(0.5);

  // Devices arrive one at a time over 1-3s; subscribing means the list fills
  // progressively instead of blocking on a settled result.
  useEffect(() => {
    if (!cast) return;
    const off = cast.onDevices((d) => {
      setDevices(d);
      setPhase((p) => (p === "scanning" && d.length ? "idle" : p));
    });
    void cast.current().then(setConnected);
    // Discover immediately rather than on first click. Without this `devices`
    // stays empty, the button renders as "no devices found" at 40% opacity,
    // and the only way to make it prominent is to click the thing you cannot
    // see. mDNS discovery is local and cheap.
    void cast.startDiscovery().catch(() => { /* firewall / no network */ });
    return off;
  }, [cast]);

  const startScan = useCallback(async () => {
    if (!cast) return;
    setPhase("scanning");
    try {
      setDevices(await cast.startDiscovery());
    } catch {
      setPhase("blocked");
    }
  }, [cast]);

  // NOTE: every hook must sit ABOVE the early return below. These were added
  // beneath it and eslint caught four rules-of-hooks violations — the same
  // defect class that crashed the app with React #300 when MiniPlayer called a
  // hook after its early returns.
  /** Push whatever is currently selected to the receiver. */
  const sendToReceiver = useCallback(async (src: NonNullable<typeof source>) => {
    if (!cast) return;
    const url = src.castUrl || src.url;
    if (!url) return;
    {
      {
          let media = toCastableUrl(url, window.location.origin);
          // The receiver fetches the stream itself, over the LAN, carrying none
          // of our cookies. Without this token it gets a 401, plays nothing, and
          // still reports the session as connected — which reads as "says
          // Playing on Living, nothing happens".
          if (src.castToken) {
            media += media.includes("?")
              ? `&t=${encodeURIComponent(src.castToken)}`
              : `?t=${encodeURIComponent(src.castToken)}`;
          }
          const bare = media.toLowerCase().split("?")[0];
          await cast.load({
            url: media,
            contentType: bare.endsWith(".m3u8") || src.isLive
              ? "application/x-mpegURL"
              : bare.endsWith(".mkv")
                ? "video/x-matroska"
                : "video/mp4",
            title: src.title,
            image: src.poster ?? undefined,
            isLive: src.isLive,
          });
      }
    }
  }, [cast]);

  // While a device is connected, EVERYTHING the app plays goes to it — pick a
  // different channel or film and the TV follows. Without this, connecting cast
  // what happened to be playing at that moment and every later selection went
  // back to playing locally, which is the opposite of what a cast session means.
  const lastSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!connected || !source) return;
    const key = `${source.castUrl || source.url}|${source.castToken ?? ""}`;
    if (lastSentRef.current === key) return;   // same source; don't restart it
    lastSentRef.current = key;
    void sendToReceiver(source).catch((e: unknown) => {
      setLoadError((e as { message?: string })?.message || "Could not play that on the device.");
      setOpen(true);
    });
  }, [connected, source, sendToReceiver]);

  // A fresh session should always push, even if it is the same source as last time.
  useEffect(() => {
    if (!connected) lastSentRef.current = null;
  }, [connected]);

  if (!isDesktop() || !cast) return null;

  const onConnect = async (d: CastDevice) => {
    setPhase("connecting");
    setLoadError(null);
    try {
      const c = await cast.connect(d);
      setConnected(c);
      setPhase("connected");
      setOpen(false);

      // Connecting alone leaves the device on its ambient screen — the whole
      // point is to move what is playing here onto it. Live channels carry a
      // dedicated `castUrl` (the server's cast HLS transcode, because the
      // default receiver cannot decode raw TS or HEVC); everything else casts
      // its normal playback URL.
      // The receiver is now the player: stand local playback down, or the
      // stream keeps running on the PC while the TV shows nothing. This also
      // makes `useMpvPlayback` inactive, so mpv stops rather than playing on
      // underneath.
      setCasting(true);

      if (source) {
        await sendToReceiver(source);
      }
    } catch (e: unknown) {
      // Swallowing this left every failure looking identical, and left the UI
      // claiming a working cast session while nothing played.
      setCasting(false);
      setPhase("idle");
      setConnected(null);
      setLoadError(
        (e as { message?: string })?.message || "Could not start playback on that device.",
      );
      setOpen(true);   // the panel was closed on connect; bring it back to show why
    }
  };


  const onDisconnect = async () => {
    await cast.disconnect();
    setConnected(null);
    setPhase("idle");
    setCasting(false);   // hand playback back to this machine
  };

  return (
    <>
      {/* 1-3 · Button: no devices / available / connected */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) void startScan();
        }}
        title={
          connected
            ? `Playing on ${connected.name}`
            : devices.length
              ? "Cast to a device"
              : "No cast devices found on this network"
        }
        aria-label="Cast"
          // A bare icon over arbitrary page content is close to invisible, so
          // it carries its own surface and always reads as a control.
          className={`w-9 h-9 flex items-center justify-center rounded-ctrl border backdrop-blur-sm transition-colors duration-press ease-bz ${
            connected
              ? "text-accent-gold bg-canvas/85 border-accent-gold/50"
              : devices.length
                ? "text-text-primary hover:text-accent-gold bg-canvas/80 border-line-strong"
                : "text-text-secondary bg-canvas/70 border-line hover:text-text-primary"
          }`}
      >
        <Cast size={18} fill={connected ? "currentColor" : "none"} />
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-[320px] rounded-panel bg-elevated border border-line-strong shadow-e4 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <span className="font-data text-[11px] uppercase tracking-[0.13em] text-text-muted">
              Cast to
            </span>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center text-text-muted hover:text-text-primary"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          {loadError && (
            <div className="px-4 py-3 border-b border-line bg-danger/10">
              <p className="text-[12.5px] font-ui text-danger">{loadError}</p>
              <p className="text-[11.5px] font-ui text-text-muted mt-1">
                The device connected but would not start the stream. Check it is on
                the same network as this PC.
              </p>
            </div>
          )}

          {/* 4 · Scanning */}
          {phase === "scanning" && devices.length === 0 && (
            <div className="px-4 py-3 space-y-2">
              <p className="text-[13px] font-ui text-text-secondary flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Looking for devices…
              </p>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-10 rounded-ctrl bg-surface animate-pulse" />
              ))}
            </div>
          )}

          {/* 8 · Discovery blocked */}
          {phase === "blocked" && (
            <div className="px-4 py-5 text-center space-y-3">
              <p className="text-[13px] font-ui text-text-primary">Can't search the network.</p>
              <p className="text-[12px] font-ui text-text-secondary">
                Allow 8K Brainzr through your firewall to find cast devices.
              </p>
              <button
                onClick={() => void startScan()}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-pill border border-line-strong text-[12px] font-ui text-text-primary hover:border-accent-gold hover:text-accent-gold"
              >
                <RefreshCw size={13} /> Scan again
              </button>
            </div>
          )}

          {/* 7 · None found */}
          {phase !== "scanning" && phase !== "blocked" && devices.length === 0 && (
            <div className="px-4 py-5 text-center space-y-3">
              <p className="text-[13px] font-ui text-text-primary">No devices found.</p>
              <p className="text-[12px] font-ui text-text-secondary">
                Your computer and the TV need to be on the same Wi-Fi network.
              </p>
              <button
                onClick={() => void cast.rescan()}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-pill border border-line-strong text-[12px] font-ui text-text-primary hover:border-accent-gold hover:text-accent-gold"
              >
                <RefreshCw size={13} /> Scan again
              </button>
            </div>
          )}

          {/* 5-6 · Found, including a device already playing something else */}
          {devices.length > 0 && (
            <ul className="max-h-[320px] overflow-y-auto py-1">
              {devices.map((d) => {
                const busy = !!d.status && !/idle/i.test(d.status);
                const isConnecting = phase === "connecting";
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      disabled={isConnecting}
                      onClick={() => void onConnect(d)}
                      className="w-full text-left px-4 py-2.5 hover:bg-surface disabled:opacity-50 flex items-center gap-3"
                    >
                      <Cast size={16} className="text-text-muted flex-shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-ui font-semibold text-text-primary truncate">
                          {d.name}
                        </span>
                        <span className="block font-data text-[11px] text-text-muted truncate">
                          {d.model} · {busy ? "Playing something else" : "Idle"}
                        </span>
                        {busy && (
                          <span className="block text-[11px] font-ui text-text-secondary">
                            Casting will take over
                          </span>
                        )}
                      </span>
                      {/* 9 · Connecting */}
                      {isConnecting && <Loader2 size={14} className="animate-spin text-accent-gold" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* 11 · Connected bar. Persistent while a session is live. */}
      {connected && (
        <div className="fixed bottom-0 left-0 right-0 z-40 h-14 bg-elevated border-t border-line-strong flex items-center gap-4 px-4 shadow-e3">
          <Cast size={16} className="text-accent-gold flex-shrink-0" fill="currentColor" />
          <span className="text-[13px] font-ui text-text-primary truncate">
            Playing on <span className="font-semibold">{connected.name}</span>
          </span>
          <div className="flex-1" />
          {/* Device volume, not app volume. Labelled with the device name so the
              two can never be confused; the local volume is hidden while
              casting rather than disabled, because it does nothing. */}
          <label className="flex items-center gap-2 text-text-secondary">
            <Volume2 size={16} />
            <span className="sr-only">{connected.name} volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => {
                const v = Number(e.target.value);
                setVolume(v);
                void cast.setVolume(v);
              }}
              className="w-28 accent-accent-gold"
            />
          </label>
          <button
            onClick={() => void onDisconnect()}
            className="px-3 h-9 rounded-pill border border-line-strong text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-primary hover:border-danger hover:text-danger"
          >
            Disconnect
          </button>
        </div>
      )}
    </>
  );
}
